import html2canvas from 'html2canvas';

/**
 * Captures the visible viewport of a canvas element using html2canvas
 * @param element - The canvas element to capture
 * @returns Promise<string | null> - Base64 encoded JPEG image without prefix, or null if failed
 */
export async function captureCanvasViewport(element: HTMLElement): Promise<string | null> {
  try {
    // Get the visible viewport dimensions
    const width = element.clientWidth;
    const height = element.clientHeight;
    const scrollLeft = element.scrollLeft;
    const scrollTop = element.scrollTop;

    // Capture the element with html2canvas
    const canvas = await html2canvas(element, {
      useCORS: true,
      backgroundColor: '#ffffff',
      scale: 0.6, // Reduce scale for smaller file size
      width,
      height,
      scrollX: -scrollLeft,
      scrollY: -scrollTop,
      logging: false // Disable logging for cleaner console
    });

    // Convert to JPEG with quality compression
    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
    
    // Remove the "data:image/jpeg;base64," prefix
    const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
    
    // Check file size (rough estimate: base64 is ~4/3 of binary size)
    const estimatedSizeKB = (base64.length * 3) / (4 * 1024);
    
    // Skip if too large (>800KB)
    if (estimatedSizeKB > 800) {
      console.warn('Vision frame too large:', estimatedSizeKB.toFixed(1), 'KB, skipping');
      return null;
    }

    return base64;
  } catch (error) {
    console.error('Failed to capture canvas viewport:', error);
    return null;
  }
}

/**
 * Captures the current screen share stream as a JPEG frame
 * @param stream - The MediaStream from screen sharing
 * @returns Promise<string | null> - Base64 encoded JPEG image without prefix, or null if failed
 */
export async function captureScreenShare(stream: MediaStream): Promise<string | null> {
  try {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      console.warn('No video track found in screen share stream');
      return null;
    }

    // Create a video element to capture the current frame
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    
    // Wait for video to be ready
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.play()
          .then(() => resolve())
          .catch(reject);
      };
      video.onerror = reject;
      
      // Timeout after 3 seconds
      setTimeout(() => reject(new Error('Video load timeout')), 3000);
    });

    // Create canvas and capture current frame
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Set canvas size to video dimensions, but cap width at 1280 for reasonable file size
    const maxWidth = 1280;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    if (videoWidth > maxWidth) {
      const aspectRatio = videoHeight / videoWidth;
      canvas.width = maxWidth;
      canvas.height = Math.round(maxWidth * aspectRatio);
    } else {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
    }

    // Draw the current video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Clean up video element
    video.pause();
    video.srcObject = null;

    // Convert to JPEG with compression
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7); // Slightly higher quality for screen content
    
    // Remove the "data:image/jpeg;base64," prefix
    const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
    
    // Check file size (rough estimate: base64 is ~4/3 of binary size)
    const estimatedSizeKB = (base64.length * 3) / (4 * 1024);
    
    // Skip if too large (>1MB for screen share)
    if (estimatedSizeKB > 1024) {
      console.warn('Screen share frame too large:', estimatedSizeKB.toFixed(1), 'KB, skipping');
      return null;
    }

    console.log('📺 Screen share frame captured:', estimatedSizeKB.toFixed(1), 'KB');
    return base64;
  } catch (error) {
    console.error('Failed to capture screen share frame:', error);
    return null;
  }
}
